/**
 * ReportGlyph — resolves a report id to its Mi6 glyph icon.
 * Usage: <ReportGlyph reportId="ae-my-pipeline" status="LIVE" />
 *
 * Color rules (LOCKED per W45.2 spec):
 *   LIVE         → teal #0F766E
 *   BETA         → blue #0B0B0F
 *   COMING SOON  → gray #6B7280 at 40% opacity
 */
import type { ReportStatus } from "@/data/reports-catalog";
import { REPORT_ICON_MAP, DEFAULT_GLYPH } from "./report-icon-map";
import {
  PipelineFlowIcon,
  ForecastIcon,
  CoverageHealthIcon,
  ActivityAuditIcon,
  DecayRiskIcon,
  VelocityIcon,
  ExpansionUpsellIcon,
  QuotaLeaderboardIcon,
  HelixIcon,
  TowerIcon,
  CheckpointStackIcon,
  EngagementIcon,
} from "./index";

type GlyphProps = { className?: string; style?: React.CSSProperties };
type GlyphComponent = (props: GlyphProps) => JSX.Element;

const GLYPH_COMPONENTS: Record<string, GlyphComponent> = {
  "pipeline-flow":    PipelineFlowIcon,
  "forecast":         ForecastIcon,
  "coverage-health":  CoverageHealthIcon,
  "activity-audit":   ActivityAuditIcon,
  "decay-risk":       DecayRiskIcon,
  "velocity":         VelocityIcon,
  "expansion-upsell": ExpansionUpsellIcon,
  "quota-leaderboard":QuotaLeaderboardIcon,
  "helix":            HelixIcon,
  "tower":            TowerIcon,
  "checkpoint-stack": CheckpointStackIcon,
  "engagement":       EngagementIcon,
};

const STATUS_STYLE: Record<ReportStatus, React.CSSProperties> = {
  "LIVE":         { color: "#0F766E", opacity: 1 },
  "BETA":         { color: "#0B0B0F", opacity: 1 },
  "COMING SOON":  { color: "#6B7280", opacity: 0.4 },
};

interface ReportGlyphProps {
  reportId: string;
  status: ReportStatus;
  /** Icon size in px — defaults to 48 (rendered inside an 80px hero band) */
  size?: number;
}

export default function ReportGlyph({ reportId, status, size = 48 }: ReportGlyphProps) {
  const glyphName = REPORT_ICON_MAP[reportId] ?? DEFAULT_GLYPH;
  const IconComponent = GLYPH_COMPONENTS[glyphName] ?? PipelineFlowIcon;
  const style = STATUS_STYLE[status];

  return (
    <IconComponent
      style={{
        width: size,
        height: size,
        display: "block",
        ...style,
      }}
    />
  );
}
